import { Add, Delete } from "@material-ui/icons";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Select from "components/select/Select";
import { Field, FieldArray, useField } from "formik";

import SelectIvr from "./components/SelectIvr";
import SelectQueue from "./components/SelectQueue";

const listPlanActions = [
  {
    label: "Phát IVR",
    value: "playIvr",
  },
  {
    label: "Bấm nhánh",
    value: "pressKey",
  },
  {
    label: "Định tuyến vào hàng đợi",
    value: "routing",
  },
  {
    label: "Lặp lại bước",
    value: "repeat",
  },
  {
    label: "Máy lẻ không nghe/bận",
    value: "busy",
  },
  {
    label: "Dirvert sang số điện thoại",
    value: "dirvert",
  },
];

const CallFlowPlanDetail = ({ formik, rootKey, title, mode }) => {
  const [field, meta] = useField({
    name: rootKey,
  });

  const isViewMode = mode === "view";

  return (
    <FieldArray
      name={`${rootKey}`}
      render={(arrayHelpers) => (
        <Stack spacing={2}>
          {!!title && (
            <Typography component="span" fontWeight={500} fontSize={16}>
              {title}
            </Typography>
          )}

          {field.value &&
            field.value.length > 0 &&
            field.value.map((_item, index) => (
              <Box key={index}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      borderRadius: 50,
                      width: 30,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: (theme) => theme.palette.primary.contrastText,
                      bgcolor: (theme) => theme.palette.primary.main,
                      fontWeight: 500,
                      zIndex: 1,
                      mt: 1.5,
                    }}>
                    {index + 1}
                  </Box>

                  <Box sx={{ width: 200 }}>
                    <Select
                      options={listPlanActions}
                      value={field.value?.[index]?.type}
                      label="Hành động"
                      onChange={(newValue) =>
                        formik.setFieldValue(`${rootKey}.${index}`, {
                          type: newValue,
                        })
                      }
                      error={meta.error?.[index]?.type}
                      showSearch={false}
                    />
                  </Box>

                  {field.value?.[index]?.type === "playIvr" && (
                    <Box width={300}>
                      <SelectIvr name={`${rootKey}.${index}.ivr`} />
                    </Box>
                  )}

                  {field.value?.[index]?.type === "routing" && (
                    <Box width={300}>
                      <SelectQueue name={`${rootKey}.${index}.queue`} />
                    </Box>
                  )}

                  {field.value?.[index]?.type === "dirvert" && (
                    <Box width={300}>
                      <Field name={`${rootKey}.${index}.numberPhone`}>
                        {({ field, meta }) => (
                          <TextField
                            label="Số điện thoại"
                            {...field}
                            error={!!meta.error}
                            helperText={meta.error}
                          />
                        )}
                      </Field>
                    </Box>
                  )}

                  {!isViewMode && (
                    <Box pt={0.5}>
                      <Tooltip title="Xóa hành động">
                        <IconButton onClick={() => arrayHelpers.remove(index)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Stack>

                <Box>
                  {field.value?.[index]?.type === "pressKey" && (
                    <ListItem>
                      <Stack spacing={2} divider={<Divider />}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography fontSize={15} fontWeight={500}>
                            Đúng nhánh
                          </Typography>
                          <FieldArray
                            name={`${rootKey}.${index}.correctKey`}
                            render={(correctKeyArrayHelpers) => (
                              <Stack spacing={2}>
                                <Field name={`${rootKey}.${index}.correctKey`}>
                                  {({ field: correctKeyField }) =>
                                    correctKeyField?.value?.map(
                                      (_item, correctKeyIndex) => (
                                        <Stack
                                          direction="row"
                                          spacing={2}
                                          key={correctKeyIndex}>
                                          <Box whiteSpace="nowrap">
                                            <Field
                                              name={`${rootKey}.${index}.correctKey.${correctKeyIndex}.key`}>
                                              {({ field, meta }) => (
                                                <TextField
                                                  label="Nhánh"
                                                  sx={{ width: 120 }}
                                                  {...field}
                                                  error={!!meta.error}
                                                  helperText={meta.error}
                                                />
                                              )}
                                            </Field>
                                            <Box
                                              mt={0.5}
                                              ml={1}
                                              display="inline-block">
                                              <Tooltip title="Xóa nhánh">
                                                <IconButton
                                                  onClick={() =>
                                                    correctKeyArrayHelpers.remove(
                                                      correctKeyIndex
                                                    )
                                                  }>
                                                  <Delete />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </Box>

                                          <Box>
                                            <CallFlowPlanDetail
                                              formik={formik}
                                              rootKey={`${rootKey}.${index}.correctKey.${correctKeyIndex}.plan`}
                                              mode={mode}
                                            />
                                          </Box>
                                        </Stack>
                                      )
                                    )
                                  }
                                </Field>

                                {!isViewMode && (
                                  <AddNewButton
                                    title="Thêm nhánh"
                                    onClick={() => {
                                      correctKeyArrayHelpers.push({
                                        key: "",
                                        plan: [{ type: "" }],
                                      });
                                    }}
                                  />
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography fontSize={15} fontWeight={500}>
                            Sai nhánh
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <CallFlowPlanDetail
                              formik={formik}
                              rootKey={`${rootKey}.${index}.wrongKey.plan`}
                              mode={mode}
                            />
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography fontSize={15} fontWeight={500}>
                            Không bấm
                          </Typography>
                          <Field
                            name={`${rootKey}.${index}.notPress.pendingTime`}>
                            {({ field, meta }) => (
                              <TextField
                                label="Sau"
                                sx={{ width: 150 }}
                                {...field}
                                type="number"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      giây
                                    </InputAdornment>
                                  ),
                                }}
                                error={!!meta.error}
                                helperText={meta.error}
                              />
                            )}
                          </Field>

                          <Box sx={{ flex: 1 }}>
                            <CallFlowPlanDetail
                              formik={formik}
                              rootKey={`${rootKey}.${index}.notPress.plan`}
                              mode={mode}
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    </ListItem>
                  )}

                  {field.value?.[index]?.type === "busy" && (
                    <ListItem>
                      <Stack direction="row" spacing={2}>
                        <Field name={`${rootKey}.${index}.pendingTime`}>
                          {({ field, meta }) => (
                            <TextField
                              label="Sau"
                              sx={{ width: 150 }}
                              {...field}
                              type="number"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    giây
                                  </InputAdornment>
                                ),
                              }}
                              error={!!meta.error}
                              helperText={meta.error}
                            />
                          )}
                        </Field>
                        <Box sx={{ flex: 1 }}>
                          <CallFlowPlanDetail
                            formik={formik}
                            rootKey={`${rootKey}.${index}.plan`}
                            mode={mode}
                          />
                        </Box>
                      </Stack>
                    </ListItem>
                  )}
                </Box>
              </Box>
            ))}

          {!isViewMode && (
            <AddNewButton
              title="Thêm kịch bản"
              onClick={() => {
                arrayHelpers.push({
                  type: "",
                });
              }}
            />
          )}
        </Stack>
      )}
    />
  );
};

const ListItem = ({ children, width }) => {
  return (
    <Box
      sx={{
        pl: "50px",
        pt: "20px",
        position: "relative",
        "::before": {
          content: '""',
          borderBottom: (theme) => `2px solid ${theme.palette.grey[300]}`,
          borderLeft: (theme) => `2px solid ${theme.palette.grey[300]}`,
          display: "block",
          width: "30px",
          position: "absolute",
          left: "15px",
          top: "0px",
          height: "100%",
        },
        width,
      }}>
      {children}
    </Box>
  );
};

const AddNewButton = ({ title, onClick, ...otherProps }) => (
  <Typography fontSize={14} {...otherProps}>
    {title}
    <Button
      sx={{
        ml: 1,
        minWidth: 0,
        width: 30,
        height: 30,
        borderRadius: "50%",
        bgcolor: (theme) => theme.palette.primary.main,
        color: (theme) => theme.palette.primary.contrastText,
        "&:hover": {
          bgcolor: (theme) => theme.palette.primary.dark,
        },
      }}
      onClick={onClick}>
      <Add />
    </Button>
  </Typography>
);

export default CallFlowPlanDetail;
